import {
  DeleteOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  RetweetOutlined,
  SearchOutlined,
  SendOutlined,
} from '@ant-design/icons';
import {
  Button,
  Col,
  Input,
  notification,
  Row,
  Space,
  Splitter,
  Tooltip,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useMapProducts } from '../../../../utils/api/mapping/useMapProducts';
import { usePublishProducts } from '../../../../utils/api/marketplaces/usePublishProducts';
import { useDownloadCSV } from '../../../../utils/api/mpCatalogue/useDownloadCSV';
import { useGetMpCatalogues } from '../../../../utils/api/mpCatalogue/useGetMpCatalogue';
import { useRemoveFromMpCategories } from '../../../../utils/api/mpCategories/useRemoveFromMpCategories';
import {
  ACTION_TYPES,
  MP_WITHOUT_BROADCAST,
} from '../../../../utils/const/mpProducts';
import {
  useMpProductsFilters,
  useSelectedMpProducts,
} from '../../../../utils/zustand';
import ConfirmActionModal from './ConfirmActionModal';
import MpProductTable from './MpProductTable';
import MpProductFilters from './filters';

const DEFAULT_SIDEBAR_WIDTH = 280;

const MpProductsList = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const { marketplace } = useParams(); // Récupérer le marketplace depuis l'URL
  // sélections multiples des produits
  const {
    selectedMpProducts,
    updateSelectedMpProducts,
    clearSelectedMpProducts,
  } = useSelectedMpProducts();
  const { filters } = useMpProductsFilters();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sizes, setSizes] = useState([DEFAULT_SIDEBAR_WIDTH, 'auto']);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentActionType, setCurrentActionType] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedMpProduct, setSelectedMpProduct] = useState(null);
  const { mapProducts } = useMapProducts();
  const { publishProducts } = usePublishProducts();
  const { removeProductsFromMpCategory } = useRemoveFromMpCategories();
  const { downloadCSV, isLoading: isDownloadingCSV } = useDownloadCSV();

  const offset = useMemo(
    () => (currentPage - 1) * pageSize,
    [currentPage, pageSize]
  );

  const { mpCatalogues, count, isLoading, refetch } = useGetMpCatalogues({
    search,
    ...filters,
    ...(marketplace && { mp_category__marketplace__name: marketplace }), // Filtrer par marketplace si présent
    offset,
    limit: pageSize,
    include_offer: true,
  });

  const marketplaceName = useMemo(() => {
    if (mpCatalogues && mpCatalogues.length > 0) {
      const mpData =
        mpCatalogues[0]?.mp_category?.marketplace ||
        mpCatalogues[0]?.store?.marketplace;
      return mpData?.name?.toLowerCase() ?? null;
    }
    return null;
  }, [mpCatalogues]);

  const mappingDisabled = useMemo(() => {
    return (
      filters.has_mapping !== true ||
      !filters.mp_category__marketplace ||
      !filters.store ||
      !filters.mp_category
    );
  }, [filters]);

  const broadcastDisabled = useMemo(() => {
    return (
      !filters.status ||
      filters.status === 'new' ||
      !filters.mp_category__marketplace ||
      !filters.store ||
      !filters.mp_category ||
      MP_WITHOUT_BROADCAST.includes(marketplaceName ?? '')
    );
  }, [filters, marketplaceName]);

  const removeFromCategoryDisabled = useMemo(() => {
    return !filters.mp_category || !filters.store;
  }, [filters]);

  const exportDisabled = useMemo(() => {
    return (
      !filters.status ||
      filters.status === 'new' ||
      !filters.mp_category__marketplace ||
      !filters.store ||
      !filters.mp_category
    );
  }, [filters]);

  // Debounce : search with React Query
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset to first page and clear selected products when search, filters, or marketplace change
  useEffect(() => {
    setCurrentPage(1);
    clearSelectedMpProducts();
  }, [search, filters, marketplace, clearSelectedMpProducts]);

  const toggleSidebar = () => {
    if (sidebarOpen) {
      setSizes([0, 'auto']);
    } else {
      setSizes([DEFAULT_SIDEBAR_WIDTH, 'auto']);
    }
    setSidebarOpen(!sidebarOpen);
  };

  const handleResize = (newSizes) => {
    setSizes(newSizes);
    // Si la taille du panneau gauche est très petite, on considère qu'il est fermé
    if (newSizes[0] < 50) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  };

  const openModal = (actionType) => {
    setCurrentActionType(actionType);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setCurrentActionType(null);
    setActionLoading(false);
  };

  const handleTransform = async () => {
    const marketplaceId = filters.mp_category__marketplace;
    const body = {
      mp_categories: [filters.mp_category],
      stores: [filters.store],
      products: selectedMpProducts.map(
        (mpProduct) => mpProduct.product.reference
      ),
    };

    setActionLoading(true);
    try {
      await mapProducts({
        marketplaceId,
        body,
      });
      notification.success({
        message: 'Transformation réussie',
        description: 'Les produits sont en cours de transformation.',
        placement: 'bottomRight',
        duration: 3,
      });
      closeModal();
    } catch (error) {
      notification.error({
        message: 'Erreur de transformation',
        description:
          error?.response?.data?.detail ||
          'Une erreur est survenue lors de la transformation des produits.',
        placement: 'bottomRight',
        duration: 3,
      });
      setActionLoading(false);
    }
  };

  const handlePublish = async () => {
    const marketplaceId = filters.mp_category__marketplace;
    const body = {
      mp_categories: [filters.mp_category],
      stores: [filters.store],
      products: selectedMpProducts.map(
        (mpProduct) => mpProduct.product.reference
      ),
      async: true,
    };
    setActionLoading(true);
    try {
      const data = await publishProducts({
        marketplaceId,
        body,
      });
      const errors = data?.errors ?? [];
      if (!errors || errors.length === 0) {
        notification.success({
          message: 'Publication réussie',
          description:
            'Les produits sont en cours de publication sur la marketplace.',
          placement: 'bottomRight',
          duration: 3,
        });
        closeModal();
      } else {
        throw errors;
      }
    } catch (error) {
      notification.error({
        message: 'Erreur de publication',
        description: Array.isArray(error)
          ? `Une erreur est survenue lors de la publication des produits : ${error
              .map((e) => (typeof e === 'string' ? e : e?.error))
              .filter(Boolean)
              .join('; ')}`
          : error?.response?.data?.detail ||
            error?.message ||
            'Une erreur est survenue lors de la publication des produits.',
        placement: 'bottomRight',
        duration: 3,
      });
      setActionLoading(false);
    }
  };

  const handleRemoveFromCategory = async () => {
    const categoryId = filters.mp_category;
    const storeId = filters.store;
    const body = {
      products: selectedMpProducts.map(
        (mpProduct) => mpProduct.product.reference
      ),
      stores: [storeId],
    };
    setActionLoading(true);
    try {
      await removeProductsFromMpCategory({ categoryId, body });
      notification.success({
        message: 'Retrait de la catégorie réussi',
        description:
          'Les produits ont été retirés de la catégorie avec succès.',
        placement: 'bottomRight',
        duration: 3,
      });
      refetch();
      clearSelectedMpProducts();
      closeModal();
    } catch (error) {
      notification.error({
        message: 'Erreur de retrait de la catégorie',
        description:
          error?.response?.data?.detail ||
          'Une erreur est survenue lors du retrait des produits de la catégorie.',
        placement: 'bottomRight',
        duration: 3,
      });
      setActionLoading(false);
    }
  };
  const handleRemoveOneFromCategory = async () => {
    const categoryId = selectedMpProduct.mp_category.id;
    const storeId = selectedMpProduct.store.id;
    const body = {
      products: [selectedMpProduct.product.reference],
      stores: [storeId],
    };
    try {
      setActionLoading(true);
      await removeProductsFromMpCategory({ categoryId, body });
      notification.success({
        message: 'Retrait de la catégorie réussi',
        description: 'Le produit a été retiré de la catégorie avec succès.',
        placement: 'bottomRight',
        duration: 3,
      });
      refetch();
      setSelectedMpProduct(null);
      closeModal();
    } catch (error) {
      notification.error({
        message: 'Erreur de retrait de la catégorie',
        description:
          error?.response?.data?.detail ||
          'Une erreur est survenue lors du retrait du produit de la catégorie.',
        placement: 'bottomRight',
        duration: 3,
      });
      setActionLoading(false);
    }
  };

  const handlePublishOne = async () => {
    console.log('publish one', selectedMpProduct);
  };
  const handleTransformOne = async () => {
    console.log('transform one', selectedMpProduct);
  };

  const handleConfirmAction = () => {
    switch (currentActionType) {
      case ACTION_TYPES.REMOVE:
        handleRemoveFromCategory();
        break;
      case ACTION_TYPES.TRANSFORM:
        handleTransform();
        break;
      case ACTION_TYPES.BROADCAST:
        handlePublish();
        break;
      case ACTION_TYPES.REMOVE_ONE_FROM_CATEGORY:
        handleRemoveOneFromCategory();
        break;
      case ACTION_TYPES.PUBLISH_ONE:
        handlePublishOne();
        break;
      case ACTION_TYPES.TRANSFORM_ONE:
        handleTransformOne();
        break;
      default:
        closeModal();
    }
  };

  // Export les produits sélectionnés en CSV
  const handleSelectedExport = async () => {
    const products = selectedMpProducts.map(
      (mpProduct) => mpProduct.product.reference
    );
    const body = {
      mp_category: filters.mp_category,
      store: filters.store,
      marketplace: filters.mp_category__marketplace,
      products,
    };
    try {
      const result = await downloadCSV(body);
      if (result.async) {
        notification.success({
          message: 'Export en cours',
          description: "Vous recevrez un email lorsque l'export sera terminé.",
          placement: 'bottomRight',
          duration: 5,
        });
      } else {
        notification.success({
          message: 'Export réussi',
          description: 'Le fichier a été exporté avec succès',
          placement: 'bottomRight',
          duration: 3,
        });
      }
    } catch (error) {
      notification.error({
        message: "Erreur d'export",
        description: error.message,
        placement: 'bottomRight',
        duration: 3,
      });
    }
  };

  return (
    <>
      <Splitter
        onResize={handleResize}
        style={{ height: 'calc(100vh - 64px)' }}
      >
        <Splitter.Panel
          size={sizes[0]}
          min={0}
          max={400}
          collapsible
          style={{
            overflow: 'auto',
            transition: 'width 0.2s ease',
          }}
        >
          <div
            style={{
              padding: sidebarOpen ? '16px' : 0,
              height: '100%',
              backgroundColor: '#fafafa',
              borderRight: '1px solid #f0f0f0',
              display: sidebarOpen ? 'block' : 'none',
            }}
          >
            <MpProductFilters />
          </div>
        </Splitter.Panel>
        <Splitter.Panel size={sizes[1]} style={{ overflow: 'auto' }}>
          <div style={{ padding: '0 16px' }}>
            <Row gutter={[16, 16]}>
              <Col span={12} justify="start" style={{ paddingLeft: 0 }}>
                <Space align="center">
                  <Button
                    type="text"
                    icon={
                      sidebarOpen ? (
                        <MenuFoldOutlined />
                      ) : (
                        <MenuUnfoldOutlined />
                      )
                    }
                    onClick={toggleSidebar}
                    title={
                      sidebarOpen ? 'Fermer le panneau' : 'Ouvrir le panneau'
                    }
                  />
                  <h1 style={{ margin: 0 }}>
                    {t('dashboard:navigation.mp-products')}
                  </h1>
                </Space>
              </Col>
            </Row>
            <div style={{ height: 16 }} />
            <Row gutter={[16, 16]} align="middle">
              <Col
                span={12}
                style={{
                  display: 'flex',
                  justifyContent: 'start',
                  margin: '8px 0',
                }}
              >
                <Input
                  size="small"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  prefix={
                    <SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} />
                  }
                  placeholder="Rechercher un produit marketplace..."
                  style={{ width: 400 }}
                  allowClear
                />
              </Col>
              <Col span={24} style={{ margin: 0 }}>
                <div
                  style={{
                    minHeight: '42px',
                    padding: '8px 8px',
                    marginBottom: '8px',
                    borderRadius: '4px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor:
                      selectedMpProducts.length > 0
                        ? '#91caff4f'
                        : 'transparent',
                  }}
                >
                  <Space>
                    <span>
                      Produits sélectionnés ({selectedMpProducts.length})
                    </span>
                    {selectedMpProducts.length > 0 && (
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => clearSelectedMpProducts()}
                      >
                        Désélectionner tout
                      </Button>
                    )}
                  </Space>
                  {selectedMpProducts.length === 0 && (
                    <Space size={24} style={{ cursor: 'help' }}>
                      <Tooltip title="Sélectionnez un store et une categorie MP pour retirer les produits de la catégorie MP">
                        Retirer de la catégorie <InfoCircleOutlined />
                      </Tooltip>
                      <Tooltip title="Sélectionnez une marketplace, un store et une categorie MP et des produits avec un mapping pour débuter la transformation des produits">
                        Transformer <InfoCircleOutlined />
                      </Tooltip>
                      <Tooltip title="Sélectionnez une marketplace, un store et une categorie MP et des produits mappés pour diffuser les produits sur une marketplace">
                        Diffuser <InfoCircleOutlined />
                      </Tooltip>
                      <Tooltip title="Sélectionnez une marketplace, un store, une catégorie MP et des produits mappés et transformés pour exporter en CSV">
                        Exporter <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  )}
                  {selectedMpProducts.length > 0 && (
                    <Space>
                      <Button
                        type="default"
                        size="small"
                        danger
                        onClick={() => openModal(ACTION_TYPES.REMOVE)}
                        disabled={removeFromCategoryDisabled}
                        icon={<DeleteOutlined />}
                      >
                        {t('common:remove-from-category')}
                      </Button>
                      <Button
                        type="default"
                        size="small"
                        onClick={() => openModal(ACTION_TYPES.TRANSFORM)}
                        disabled={mappingDisabled}
                        icon={<RetweetOutlined />}
                      >
                        {t('common:transform-products')}
                      </Button>
                      <Button
                        type="default"
                        size="small"
                        onClick={() => openModal(ACTION_TYPES.BROADCAST)}
                        disabled={broadcastDisabled}
                        icon={<SendOutlined />}
                      >
                        {t('common:broadcast')}
                      </Button>
                      <Button
                        type="primary"
                        size="small"
                        onClick={handleSelectedExport}
                        disabled={exportDisabled}
                        loading={isDownloadingCSV}
                        icon={<DownloadOutlined />}
                      >
                        Exporter ({selectedMpProducts.length})
                      </Button>
                    </Space>
                  )}
                </div>
              </Col>
            </Row>
            <MpProductTable
              mappingDisabled={mappingDisabled}
              selectedProducts={selectedMpProducts}
              updateSelectedMpProducts={updateSelectedMpProducts}
              mpCatalogues={mpCatalogues}
              count={count}
              isLoading={isLoading}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              onRemoveOneFromCategory={(mpProduct) => {
                setSelectedMpProduct(mpProduct);
                openModal(ACTION_TYPES.REMOVE_ONE_FROM_CATEGORY);
              }}
              onPublishOne={(mpProduct) => {
                setSelectedMpProduct(mpProduct);
                openModal(ACTION_TYPES.PUBLISH_ONE);
              }}
              onTransformOne={(mpProduct) => {
                setSelectedMpProduct(mpProduct);
                openModal(ACTION_TYPES.TRANSFORM_ONE);
              }}
            />
          </div>
        </Splitter.Panel>
      </Splitter>
      <ConfirmActionModal
        open={modalOpen}
        onCancel={closeModal}
        onConfirm={handleConfirmAction}
        actionType={currentActionType}
        productCount={selectedMpProducts.length}
        loading={actionLoading}
      />
    </>
  );
};

export default MpProductsList;
